import React, { useState, useEffect } from 'react';
import { Application, CriticalApp } from '../types';
import { applicationsApi, getAuthToken } from '../services/api';
import { ApplicationsTable } from '../components/applications/ApplicationsTable';
import { AddApplicationButton } from '../components/applications/AddApplicationButton';
import { AddApplicationCard } from '../components/applications/AddApplicationCard';
import { AddApplicationModal } from '../components/applications/AddApplicationModal';
import { EditApplicationModal } from '../components/applications/EditApplicationModal';
import { NotesModal } from '../components/applications/NotesModal';
import { toast } from 'react-toastify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildApiUrl, API_ENDPOINTS } from '../utils/api-config';
import { HelpButton } from '../components/common/HelpButton';
import { useCustomer } from '../context/CustomerContext';

interface ApplicationsProps {
    onShowHelp?: () => void;
}

export const Applications: React.FC<ApplicationsProps> = ({ onShowHelp }) => {
    const { customerData, updateCustomerData } = useCustomer();
    const [applications, setApplications] = useState<Application[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
    const [selectedNotes, setSelectedNotes] = useState<string | null>(null);
    const [selectedNotesTitle, setSelectedNotesTitle] = useState<string>('');

    // Load applications from customerData context
    useEffect(() => {
        setIsLoading(true);
        
        try {
            // Load from customerData context
            if (customerData?.critical_apps?.critical_apps_list && customerData.critical_apps.critical_apps_list.length > 0) {
                // Map the critical apps to the Application interface
                type AppWithOptionalId = CriticalApp & { application_id?: string };
                const mappedApps: Application[] = customerData.critical_apps.critical_apps_list
                    .filter(app => app.name) // Filter out apps without a name
                    .map((app, index) => {
                        const appWithId = app as AppWithOptionalId;
                        const resolvedAppId = appWithId.application_id ?? app.app_id;
                        return {
                            id: resolvedAppId ?? `app-${index}`,
                            app_id: resolvedAppId,
                            entry: app.entry,
                            name: app.name,
                            usage: app.usage,
                            location: app.location,
                            tier: app.tier,
                            customer_facing_notes: app.customer_facing_notes,
                            internal_notes: app.internal_notes
                        };
                    });
                
                setApplications(mappedApps);
            } else {
                // If no customer data is available, set empty applications array
                setApplications([]);
            }
        } catch (error) {
            console.error('Error processing applications data:', error);
            // Set empty array in case of error
            setApplications([]);
        } finally {
            setIsLoading(false);
        }
    }, [customerData]);

    const handleEditApplication = (application: Application) => {
        setSelectedApplication(application);
        setIsEditModalOpen(true);
    };
    
    const handleViewNotes = (notes: string | null, title: string) => {
        setSelectedNotes(notes);
        setSelectedNotesTitle(title);
        setIsNotesModalOpen(true);
    };
    
    const handleDeleteApplication = async (applicationId: string) => {
        try {
            const appToDelete = applications.find(app => app.id === applicationId || app.app_id === applicationId);
            if (!appToDelete) {
                throw new Error('Application not found');
            }

            if (customerData?.customer_id && appToDelete.app_id) {
                await applicationsApi.deleteApplication(customerData.customer_id, appToDelete.app_id);
            }

            const updatedApps = applications.filter(app => app.id !== appToDelete.id && app.app_id !== appToDelete.app_id);
            setApplications(updatedApps);

            if (customerData) {
                const updatedCriticalApps = customerData.critical_apps?.critical_apps_list?.filter(app =>
                    app.app_id !== appToDelete.app_id
                ) ?? [];
                updateCustomerData({
                    critical_apps: {
                        ...customerData.critical_apps,
                        critical_apps_list: updatedCriticalApps,
                    },
                });
            }
            toast.success('Application deleted successfully!');
        } catch (error) {
            console.error('Error deleting application:', error);
            toast.error('Failed to delete application. Please try again.');
        }
    };
    
    const handleUpdateApplication = async (updatedApplication: Application) => {
        try {
            if (!customerData?.customer_id) {
                const updatedApps = applications.map(app =>
                    app.id === updatedApplication.id ? updatedApplication : app
                );
                setApplications(updatedApps);
                toast.success('Application updated successfully!');
                return;
            }
            if (!updatedApplication.app_id) {
                toast.error('Cannot update: application id is missing.');
                return;
            }
            const body = {
                name: updatedApplication.name,
                usage: updatedApplication.usage,
                location: updatedApplication.location,
                tier: updatedApplication.tier,
                customer_facing_notes: updatedApplication.customer_facing_notes,
                internal_notes: updatedApplication.internal_notes,
            };
            await applicationsApi.updateApplication(customerData.customer_id, updatedApplication.app_id, body);

            const updatedApps = applications.map(app =>
                app.id === updatedApplication.id || app.app_id === updatedApplication.app_id
                    ? { ...updatedApplication, app_id: updatedApplication.app_id }
                    : app
            );
            setApplications(updatedApps);

            if (customerData) {
                type AppWithOptionalId = CriticalApp & { application_id?: string };
                const updatedCriticalApps: CriticalApp[] = (customerData.critical_apps?.critical_apps_list ?? []).map(app => {
                    const appWithId = app as AppWithOptionalId;
                    const isMatch = app.app_id === updatedApplication.app_id || appWithId.application_id === updatedApplication.app_id;
                    return isMatch
                        ? {
                            ...app,
                            name: updatedApplication.name,
                            usage: updatedApplication.usage,
                            location: updatedApplication.location,
                            tier: updatedApplication.tier,
                            customer_facing_notes: updatedApplication.customer_facing_notes,
                            internal_notes: updatedApplication.internal_notes,
                            app_id: updatedApplication.app_id,
                            entry: app.entry,
                        }
                        : app;
                });
                updateCustomerData({
                    critical_apps: {
                        ...customerData.critical_apps,
                        critical_apps_list: updatedCriticalApps,
                    },
                });
            }
            toast.success('Application updated successfully!');
        } catch (error) {
            console.error('Error updating application:', error);
            toast.error('Failed to update application. Please try again.');
        }
    };
    
    const handleAddApplication = async (newApplication: Omit<Application, 'id'>) => {
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (customerData?.customer_id) {
                // Make a POST request to the API
                const { userId } = await getAuthToken();
                const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_APPLICATION_CREATE(customerData.customer_id)), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'user-id': userId,
                        Authorization: `Bearer ${idToken}`
                    },
                    body: JSON.stringify({
                        entry: newApplication.entry,
                        name: newApplication.name,
                        usage: newApplication.usage,
                        location: newApplication.location,
                        tier: newApplication.tier,
                        customer_facing_notes: newApplication.customer_facing_notes,
                        internal_notes: newApplication.internal_notes,
                        app_id: newApplication.app_id // Include app_id if available for editing existing applications
                    }),
                });
                
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`Failed to add application: ${response.status} ${response.statusText} - ${errorText}`);
                    throw new Error(`Failed to add application: ${response.status} ${response.statusText}`);
                }
                
                const responseData = await response.json();
                const resolvedAppId = responseData.id ?? responseData.application_id ?? responseData.application?.app_id ?? responseData.app_id;

                // Add the new application to the local state with id and app_id from API response
                const addedApplication = {
                    id: resolvedAppId ?? `app-${applications.length}`,
                    app_id: resolvedAppId,
                    ...newApplication
                };
                setApplications([...applications, addedApplication]);

                // Update context with the new application
                if (customerData) {
                    const newCriticalApp: CriticalApp = {
                        entry: newApplication.entry || '',
                        name: newApplication.name,
                        usage: newApplication.usage,
                        location: newApplication.location,
                        tier: newApplication.tier,
                        customer_facing_notes: newApplication.customer_facing_notes,
                        internal_notes: newApplication.internal_notes,
                        app_id: resolvedAppId
                    };
                    
                    updateCustomerData({
                        critical_apps: {
                            ...customerData.critical_apps,
                            critical_apps_list: [...(customerData.critical_apps?.critical_apps_list || []), newCriticalApp]
                        }
                    });
                }
                
                // Show success toast
                toast.success('Application added successfully!');
            } else {
                // Fallback to the mock API if no customer ID is available
                const addedApplication = await applicationsApi.addApplication(newApplication, customerData?.customer_id || 'temp-customer-id');
                setApplications([...applications, addedApplication]);
                
                // Show success toast
                toast.success('Application added successfully!');
            }
        } catch (error) {
            console.error('Error adding application:', error);
            toast.error('Failed to add application. Please try again.');
        }
    };


    return (

        <div className='px-4'>
            <div className="flex justify-between items-center mb-6">
                <div className='flex gap-3 items-center'>
                    <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Top 10 Critical Applications</h1>
                    {onShowHelp && <HelpButton onClick={onShowHelp} />}
                </div>
                <div>
                    {applications.length > 0 && (
                        <AddApplicationButton onClick={() => setIsAddModalOpen(true)} />
                    )}
                </div>
            </div>

            <div className=" mx-auto px-4 py-6" style={{
                background: 'linear-gradient(135deg, rgba(255,240,255,0.2) 0%, rgba(255,255,255,0) 50%)'
            }}>
                {isLoading ? (
                    <div className="flex justify-center py-10 items-center h-full">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#002D62]"></div>
                    </div>
                ) : applications.length === 0 ? (
                    <div className="flex justify-center items-center py-10">
                        <AddApplicationCard onClick={() => setIsAddModalOpen(true)} />
                    </div>
                ) : (
                    <ApplicationsTable
                        applications={applications}
                        onEditApplication={handleEditApplication}
                        onViewNotes={handleViewNotes}
                    />
                )}

                {/* Add Application Modal */}
                <AddApplicationModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onSave={handleAddApplication}
                />


                {/* Edit Application Modal */}
                <EditApplicationModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={handleUpdateApplication}
                    onDelete={handleDeleteApplication}
                    application={selectedApplication}
                />

                {/* Notes View Modal */}
                <NotesModal
                    isOpen={isNotesModalOpen}
                    onClose={() => setIsNotesModalOpen(false)}
                    notes={selectedNotes}
                    title={selectedNotesTitle}
                />
            </div>

        </div>
    );
};
